import React from "react";
import { handleSmartLink } from "../../utils/linkHandler";
import { extractValueFromDeepLink } from "../../utils/linkValidation";
import { extractProtocol } from "../../utils/deepLinkHelpers";
import { findTemplateByProtocol } from "../../utils/communicationTemplates";
import { useCardPreview } from "../../contexts/CardPreviewContext";

const SAFE_PROTOCOLS = new Set(["http", "https", "mailto", "tel", "sms"]);

const deriveFallbackHref = (href: string): string | undefined => {
  if (!href) {
    return undefined;
  }

  try {
    const url = new URL(href);
    const protocol = url.protocol.replace(":", "").toLowerCase();

    if (SAFE_PROTOCOLS.has(protocol)) {
      return undefined;
    }

    if (!url.host) {
      return undefined;
    }

    const path = `${url.pathname || ""}${url.search || ""}${url.hash || ""}`;
    return `https://${url.host}${path}`;
  } catch (error) {
    return undefined;
  }
};

const resolveFallbackHref = (
  href: string,
  providedFallback?: string
): string | undefined => {
  const explicitFallback = providedFallback?.trim();
  if (explicitFallback) {
    return explicitFallback;
  }

  const protocol = extractProtocol(href);
  if (protocol) {
    const template = findTemplateByProtocol(protocol);
    if (template?.fallbackLink) {
      try {
        const value = extractValueFromDeepLink(href, template);
        if (value) {
          const generated = template.fallbackLink(value).trim();
          if (generated) {
            return generated;
          }
        }
      } catch (error) {}
    }
  }

  return deriveFallbackHref(href);
};

interface SmartLinkProps {
  href: string;
  fallbackHref?: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  title?: string;
  target?: string;
  rel?: string;
  dir?: string;
}

/**
 * SmartLink Component
 *
 * A link component that intelligently handles deep links with fallback support.
 * - On desktop: Always uses HTTPS fallback to prevent blank pages
 * - On mobile: Attempts deep link with timeout-based fallback
 *
 * @example
 * ```tsx
 * <SmartLink
 *   href="whatsapp://send?phone=1234567890"
 *   fallbackHref="https://wa.me/1234567890"
 *   className="button-class"
 * >
 *   Open WhatsApp
 * </SmartLink>
 * ```
 */
export const SmartLink: React.FC<SmartLinkProps> = ({
  href,
  fallbackHref,
  children,
  className = "",
  onClick,
  title,
  target = "_blank",
  rel = "noopener noreferrer",
  dir,
}) => {
  const isPreview = useCardPreview();

  if (isPreview) {
    return (
      <span className={className} title={title} dir={dir}>
        {children}
      </span>
    );
  }

  const resolvedFallback = resolveFallbackHref(href, fallbackHref);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(e);
    }

    if (e.defaultPrevented) {
      return;
    }

    const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    const isLeftButton = e.button === 0;

    if (!isLeftButton || isModifiedClick) {
      return;
    }

    if (resolvedFallback && resolvedFallback !== href) {
      e.preventDefault();
      const shouldOpenInNewTab = target === "_blank";
      handleSmartLink(href, resolvedFallback, undefined, shouldOpenInNewTab);
    }
  };

  return (
    <a
      href={resolvedFallback || href}
      onClick={handleClick}
      className={className}
      title={title}
      target={target}
      rel={rel}
      dir={dir}
    >
      {children}
    </a>
  );
};

export default SmartLink;
